/**
 * Log or edit an activity — a call, email, meeting, demo or note.
 *
 * Opened from a deal it is the whole "I have just worked this lead" form,
 * not only a diary entry: the two things somebody changes after a call are
 * where the lead now sits and who is carrying it, and making them go and
 * find the deal sheet afterwards is how a board ends up with a trail of
 * calls and every row still at New.
 *
 * So Stage and Owner are here, and they write through to the deal itself —
 * the same values the edit sheet shows, because they are the same fields.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { formatDate, toInputDate, fromInputDate } from '../../lib/dates.js';
import { salesTeam, pipelineStages, isDeal, dealActivities } from '../../domain/crm.js';

import Icon from '../../lib/icons.jsx';
import Sheet, { Group, Field, OptionSelect } from '../Sheet.jsx';
import DealJourney from '../DealJourney.jsx';

export default function ActivitySheet({ id, dealId, clientId, onClose }) {
  const {
    S, crm, rows, saveActivity, saveDeal, noteActivity, logDealEvent, convertWon, toast, me
  } = useApp();
  const existing = id ? crm.acts.find(a => a.id === id) : null;

  /*
   * Against an account, where there is no deal to hang it on.
   *
   * A trial being chased has no pipeline lead behind it — most accounts
   * registered straight off the website — so the form opened with "DEAL —"
   * and whatever was written went nowhere anybody could find it again. The
   * activities table has carried `client_id` all along; nothing was passing
   * one in.
   */
  const onClientId = clientId || existing?.clientId || '';
  const client = onClientId ? rows.find(x => x.id === onClientId) : null;

  /*
   * The deal is not a field when it is already known.
   *
   * This opens from a deal far more often than it opens loose — off the
   * journey, off the deal sheet — and there the picker could only ever be
   * used to move the activity onto the wrong deal. It stays a picker on
   * the Activities page, where there is nothing to lock it to.
   */
  const fixed = dealId || existing?.dealId || '';
  const locked = !!fixed || !!onClientId;
  const onDeal = locked ? crm.deals.find(d => d.id === fixed) : null;

  /* After onDeal, not before it: the row's own owner is passed in so a
     name that is no longer on the Owners list is still offered here. */
  const team = salesTeam(S, crm, onDeal?.owner);

  /* Won and Lost are not on the board's list, and a row that has reached
     Deals is past the point of being moved between stages from here. */
  const stages = onDeal && !isDeal(onDeal)
    ? [...pipelineStages(S), 'Won'] : [];

  const [form, setForm] = useState(() => existing || {
    subject: '',
    type: 'Call',
    /*
     * Whoever is signed in, then whoever is carrying the row.
     *
     * It was `S.senderName` — the one name outgoing email is signed with for
     * the whole workspace — so every call anybody logged was offered up as
     * Agalya's, on leads belonging to thirteen other people. The person at
     * the keyboard is the one making the call; the lead's own owner is the
     * next best guess when they are not on the sales list.
     *
     * Blank rather than a name the dropdown does not offer, because a value
     * that is not in the list reads as a bug in the list.
     */
    who: [(me && me.name), onDeal?.owner, S.senderName].find(n => n && team.includes(n)) || '',
    dealId: dealId || '',
    due: formatDate(new Date(), false, S.dateFmt),
    notes: '',
    done: false
  });

  /* Seeded from the deal, so opening the form and saving it unchanged
     leaves the deal exactly as it was. */
  const [stage, setStage] = useState(() => onDeal?.stage || '');
  const [owner, setOwner] = useState(() => onDeal?.owner || '');

  /*
   * Nobody logs work on a lead nobody owns.
   *
   * The first thing that happens to a lead is somebody taking it on, and
   * until that is answered a trail of calls belongs to no one — which is
   * how a board ends up with a hundred activities and every row still
   * saying UNOWNED. So an unowned lead asks the question first, and the
   * log form only appears once it has an answer.
   *
   * After that the lead is theirs: the Owner field below is read-only,
   * and handing it to somebody else is a change to the deal, made on the
   * deal, not a side effect of writing down a phone call.
   */
  /*
   * Asked once, even if the answer did not stick.
   *
   * An owner is stored as a user id, so a name with no user behind it is
   * dropped on the way to the server: the lead read as Swathi's, came back
   * unowned, and the next call asked again — the trail ended up with "Taken
   * on by Swathi" and "Taken on by Super Admin" against one lead. The note
   * is the record that the question was answered, so it is what decides.
   */
  const takenOn = !!onDeal && dealActivities(crm, onDeal.id).some(a => /^Taken on by /i.test(a.subject || ''));
  const [asking, setAsking] = useState(() => !!onDeal && !onDeal.owner && !takenOn);
  const [taking, setTaking] = useState('');

  const takeOn = () => {
    if (!taking) { toast('Who is taking it on?'); return; }
    saveDeal(onDeal.id, { ...onDeal, owner: taking });
    noteActivity(onDeal.id, `Taken on by ${taking}`, 'Note', taking);
    logDealEvent(onDeal.id, 'owner', { from: onDeal.owner || '', to: taking, actor: taking });
    setOwner(taking);
    setAsking(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    if (!String(form.subject).trim()) { toast('What is the activity?'); return; }

    /* Against a deal there is one owner, not two: the person carrying the
       lead is the person who made the call, and asking twice would only
       produce a trail that disagrees with the row it hangs off. */
    saveActivity(id, {
      ...form,
      /* After the spread, not before it: `form` carries no clientId, and
         spreading it over this one put an undefined back on top. */
      clientId: onClientId || form.clientId || '',
      who: onDeal ? owner : form.who,
      subject: String(form.subject).trim()
    });

    /* Then the lead's stage, but only where it actually moved — an
       untouched save must not write a "Moved to New" into the trail. The
       owner is not here: it was settled before the form opened, and
       changing it is a change to the deal. */
    if (onDeal && stage && stage !== onDeal.stage) {
      saveDeal(onDeal.id, { ...onDeal, stage });
      noteActivity(onDeal.id, `Moved to ${stage}`);
      logDealEvent(onDeal.id, stage === 'Won' ? 'won' : 'stage',
        { from: onDeal.stage, to: stage, actor: owner });
      /* Won is the row leaving the board for Deals, which is a move the
         store makes, not a field the form can set on its own. */
      if (stage === 'Won') convertWon(onDeal.id);
    }

    onClose();
  };

  /* ---- the question that comes before the form */
  if (asking) {
    return (
      <Sheet
        icon="users"
        tone="g-amber"
        title="Who is taking this on?"
        onClose={onClose}
        onSave={takeOn}
        saveLabel="Take it on"
        compact
      >
        {/* Not an input. A box you cannot type in still looks like one you
            ought to try, and this is a fact about the lead, not a field. */}
        <Field label="Lead" wide>
          <span className="fread"><Icon name="zap" />{onDeal.title}</span>
        </Field>
        <Field label="Owner" wide>
          <OptionSelect value={taking} onChange={setTaking} options={team} blank="Pick somebody" />
        </Field>
        <Field label="" wide>
          <p className="ashint">
            Nobody owns this lead yet. From here the journey is theirs — every call
            logged against it will be in their name, and handing it on later is a
            change to the deal itself.
          </p>
        </Field>
      </Sheet>
    );
  }

  return (
    <Sheet
      icon="cal"
      tone="g-pink"
      title={id ? 'Edit activity' : 'Log activity'}
      onClose={onClose}
      onSave={save}
      saveLabel="Save activity"
    >
      {/*
        * What has already happened, above the box for what happens next.
        *
        * The form opened on a blank Subject with no sign of whether this lead
        * had been rung twice or never touched — so the answer was to close
        * it, read the trail, and open it again. It is the same trail the row
        * and the lead view show, read-only here: this sheet is for adding to
        * it, and editing another entry from inside the form for a third would
        * be two half-finished records.
        */}
      {onDeal ? (
        <>
          <Group>So far</Group>
          <Field label="" wide>
            <DealJourney deal={onDeal} limit={5} />
          </Field>
        </>
      ) : null}

      <Group>{onDeal ? 'And now' : 'Activity'}</Group>

      <Field label="Subject" wide>
        <input autoFocus value={form.subject || ''} onChange={e => set('subject', e.target.value)} />
      </Field>
      <Field label="Type">
        <OptionSelect value={form.type} onChange={v => set('type', v)} options={S.actTypes || []} />
      </Field>
      <Field label={onClientId && !fixed ? 'Client' : 'Deal'}>
        {locked ? (
          <span className="fread">
            <Icon name={onClientId && !fixed ? 'users' : 'zap'} />
            {onDeal
              ? onDeal.title
              : (client ? (client.name || client.email) : (onClientId ? 'this client' : 'this deal'))}
          </span>
        ) : (
          <select value={form.dealId || ''} onChange={e => set('dealId', e.target.value)}>
            <option value="">—</option>
            {crm.deals.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        )}
      </Field>
      {/* Date and Stage together: they are the two halves of "when, and
          where does that leave it" — the pair somebody sets in one breath
          after a call. Stage was in a section of its own at the bottom,
          which put a scroll between them. */}
      <Field label="Date">
        <input
          type="date"
          value={toInputDate(form.due)}
          onChange={e => set('due', fromInputDate(e.target.value, S.dateFmt))}
        />
      </Field>
      {stages.length ? (
        <Field label="Stage">
          <OptionSelect value={stage} onChange={setStage} options={stages} />
        </Field>
      ) : null}

      {/* Read-only: the lead was taken on, and it stays theirs until
          somebody hands it over on the deal. A dropdown here would let a
          phone call quietly reassign the whole journey. */}
      <Field label="Owner">
        {onDeal ? (
          <span className={`fread ${owner ? 'set' : 'unset'}`}>
            <Icon name="users" />{owner || 'Unowned'}
          </span>
        ) : (
          <OptionSelect value={form.who} onChange={v => set('who', v)} options={team} blank="" />
        )}
      </Field>
      <Field label="">
        <div className="checks">
          <label>
            <input type="checkbox" checked={!!form.done} onChange={e => set('done', e.target.checked)} />
            Completed
          </label>
        </div>
      </Field>

      <Field label="Notes" wide>
        <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
      </Field>

      {onDeal ? (
        <Field label="" wide>
          <p className="ashint">
            The stage is the deal&rsquo;s own — changing it here moves the row and writes
            into the journey. To hand the lead to somebody else, open <b>Edit deal</b>.
          </p>
        </Field>
      ) : null}
    </Sheet>
  );
}
